/**
 * Trial-mode visual primitives — used throughout the app to enforce
 * PlanHub-style restrictions during the 14-day free trial:
 *
 * 1. <LockedField>      — wraps a value; renders a blurred placeholder + lock
 *                         icon when isLocked=true.
 * 2. <TrialBanner>      — top banner with countdown + Upgrade CTA.
 * 3. <UpgradeModal>     — global modal triggered by export buttons.
 * 4. blockExportIfTrial(onTrial, navigate) — guard helper.
 */
import { useEffect, useState } from "react";
import { Lock, Sparkles, X, ArrowRight, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// ---------------------------------------------------------------------------

/**
 * <LockedField value="123 Main St" isLocked={onTrial} reason="address" />
 *
 * Renders the value normally when not locked. When locked, shows a blurred
 * placeholder of the same width with a tiny lock icon overlay; clicking
 * triggers the upgrade modal via prop callback (or default toast).
 */
export function LockedField({ value, isLocked, onUnlockClick, className = "", placeholder = "•••••• ••••••• ••" }) {
  if (!isLocked) return <span className={className}>{value}</span>;
  return (
    <span
      data-testid="locked-field"
      title="Upgrade to unlock"
      onClick={onUnlockClick || (() => toast.info("Upgrade your plan to unlock this field."))}
      className={`inline-flex items-center gap-1 cursor-pointer select-none ${className}`}
    >
      <span
        aria-hidden="true"
        className="inline-block bg-slate-200 dark:bg-slate-700 text-transparent rounded blur-[3px] tracking-widest"
        style={{ filter: "blur(3px)" }}
      >
        {placeholder}
      </span>
      <Lock className="w-3 h-3 text-amber-600 shrink-0" />
    </span>
  );
}

/**
 * Plain blur on any block element (e.g. address line that spans 2 lines).
 * The lock icon sits in the corner and the entire block is click-to-upgrade.
 */
export function LockedBlock({ children, isLocked, label = "Upgrade to unlock", onUnlockClick }) {
  if (!isLocked) return children;
  return (
    <div
      className="relative rounded-md overflow-hidden cursor-pointer group"
      onClick={onUnlockClick}
      data-testid="locked-block"
    >
      <div aria-hidden className="select-none pointer-events-none filter blur-md saturate-50">
        {children}
      </div>
      <div className="absolute inset-0 bg-white/40 group-hover:bg-white/60 backdrop-blur-sm flex items-center justify-center gap-1.5 transition">
        <Lock className="w-3.5 h-3.5 text-amber-700" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">{label}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Top banner — appears on Procurement Scanner, Prospecting and Kanban while
 * the user is on trial. Shows the days remaining + Upgrade CTA.
 */
export function TrialBanner({ daysRemaining, onUpgrade }) {
  return (
    <div
      data-testid="trial-banner"
      className="rounded-lg bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white px-4 py-3 flex items-center gap-3 mb-3 shadow-sm border border-amber-500/30"
    >
      <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] leading-tight">
          You're on the <b className="text-amber-300">14-day free trial</b>
          {typeof daysRemaining === "number" && daysRemaining > 0 && (
            <> · <span className="text-amber-200">{daysRemaining} day{daysRemaining === 1 ? "" : "s"} left</span></>
          )}
          . Project Location, Status Updates, Special Instructions, Project Files, Subcontractors and General Contractors are <b>locked</b>. Downloads are disabled.
        </div>
      </div>
      <button
        type="button"
        onClick={onUpgrade}
        data-testid="trial-banner-upgrade"
        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-[12px] font-bold uppercase tracking-wide"
      >
        <Sparkles className="w-3.5 h-3.5" /> Upgrade <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Global upgrade modal — fires from any export button when the user is
 * mid-trial. Listens for the custom `agentelead:trial-upgrade` event so
 * any component can call `dispatchEvent` without prop-drilling.
 *
 * Renders a "before/after" preview of 3 hot leads — blurred on the left
 * (what the user sees today) vs. revealed on the right (what they unlock
 * by upgrading). Turns the paywall into a value spoiler.
 */
export function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("download leads");
  const [previewLeads, setPreviewLeads] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [ctx, setCtx] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      setReason((e?.detail?.reason) || "download leads");
      setPreviewLeads([]);  // force re-fetch with the latest search context
      setOpen(true);
    };
    window.addEventListener("agentelead:trial-upgrade", handler);
    return () => window.removeEventListener("agentelead:trial-upgrade", handler);
  }, []);

  // Lazy-load demo leads the first time the modal opens, personalized to
  // the user's last search context (sector / city / keyword) when available.
  useEffect(() => {
    if (!open || previewLeads.length > 0) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const searchCtx = readSearchContext();
    setCtx(searchCtx);
    const params = new URLSearchParams();
    if (searchCtx.sector) params.set("sector", searchCtx.sector);
    if (searchCtx.city) params.set("city", searchCtx.city);
    if (searchCtx.keyword) params.set("keyword", searchCtx.keyword);
    const qs = params.toString();
    fetch(`${API}/trial-gate/preview-leads${qs ? "?" + qs : ""}`)
      .then((r) => r.ok ? r.json() : { leads: [], personalized: false })
      .then((d) => {
        setPreviewLeads(d.leads || []);
        setPersonalized(!!d.personalized);
      })
      .catch(() => setPreviewLeads([]));
  }, [open, previewLeads.length]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={() => setOpen(false)}
      data-testid="upgrade-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 relative my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="upgrade-modal"
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100"
          data-testid="upgrade-modal-close"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[20px] font-bold text-slate-900 mb-1">Upgrade to {reason}</h3>
            <p className="text-[13px] text-slate-600 leading-relaxed">
              Downloads (CSV, Excel, PDF, Google Sheets, JSON) are disabled during the 14-day free trial.
              See exactly what unlocks below.
            </p>
          </div>
        </div>

        {/* Before / after preview — each row shows the same lead twice,
            blurred on the left and revealed on the right. */}
        {previewLeads.length > 0 && (
          <div className="rounded-xl border border-slate-200 overflow-hidden mb-5" data-testid="upgrade-preview">
            {personalized && (ctx.sector || ctx.city || ctx.keyword) && (
              <div className="bg-emerald-50 border-b border-emerald-200 px-3 py-2 flex items-center gap-2 text-[11px] font-semibold text-emerald-800"
                   data-testid="upgrade-preview-personalized">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>
                  Real leads from your search
                  {ctx.sector && <> · <b>{ctx.sector}</b></>}
                  {ctx.city && <> in <b>{ctx.city}</b></>}
                </span>
                <span className="ml-auto text-[10px] text-emerald-600/70 font-medium">
                  These are in your pipeline NOW
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 text-[10.5px] font-bold uppercase tracking-wider">
              <div className="bg-slate-100 text-slate-500 px-3 py-2 inline-flex items-center gap-1.5">
                <Lock className="w-3 h-3" /> Today (locked)
              </div>
              <div className="bg-emerald-50 text-emerald-700 px-3 py-2 inline-flex items-center gap-1.5 border-l border-slate-200">
                <Sparkles className="w-3 h-3" /> After upgrade
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {previewLeads.map((ld) => (
                <div key={ld.name} className="grid grid-cols-2">
                  <div className="px-3 py-2.5 bg-slate-50/50">
                    <div className="text-[12.5px] font-bold text-slate-800 truncate">{ld.name}</div>
                    <div className="text-[10.5px] text-slate-500 mb-1">{ld.sector} · {ld.city}</div>
                    <div className="space-y-0.5 text-[11.5px]">
                      <BlurredRow label="📞" />
                      <BlurredRow label="✉" />
                      <BlurredRow label="📍" />
                    </div>
                  </div>
                  <div className="px-3 py-2.5 border-l border-slate-200 bg-emerald-50/30">
                    <div className="text-[12.5px] font-bold text-slate-900 truncate">{ld.name}</div>
                    <div className="text-[10.5px] text-slate-500 mb-1">{ld.sector} · {ld.city}</div>
                    <div className="space-y-0.5 text-[11.5px] font-mono tabular-nums">
                      <div className="truncate">📞 <span className="text-slate-800">{ld.phone}</span></div>
                      <div className="truncate">✉ <span className="text-slate-800">{ld.email}</span></div>
                      <div className="truncate">📍 <span className="text-slate-700">{ld.address}</span></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2 mb-5">
          {[
            "Unlock contact details, addresses and bid documents",
            "Export to CSV, Excel, PDF and Google Sheets",
            "Sync leads to your CRM and outreach tools",
            "Cancel anytime, no commitment",
          ].map((line) => (
            <div key={line} className="flex items-start gap-2 text-[13px] text-slate-700">
              <Sparkles className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
              <span>{line}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); navigate("/billing"); }}
          data-testid="upgrade-modal-cta"
          className="w-full h-11 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 text-[14px] font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30"
        >
          See plans <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="w-full h-9 mt-2 text-[12.5px] text-slate-500 hover:text-slate-700"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}

// A single blurred row inside the "Today (locked)" column. The bar widths
// vary slightly so the placeholders feel real — not just identical strips.
function BlurredRow({ label, width = "70%" }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="opacity-70">{label}</span>
      <span
        className="inline-block h-2.5 rounded bg-gradient-to-r from-slate-300 to-slate-400/60"
        style={{ width, filter: "blur(1.5px)" }}
        aria-hidden
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Guard used inline by export handlers:
 *
 *   const exportCsv = () => {
 *     if (blockExportIfTrial(onTrial, "export CSV")) return;
 *     // ... actual CSV download ...
 *   };
 */
export function blockExportIfTrial(onTrial, reason = "download leads") {
  if (!onTrial) return false;
  window.dispatchEvent(new CustomEvent("agentelead:trial-upgrade", { detail: { reason } }));
  return true;
}

// ---------------------------------------------------------------------------

/**
 * Tracks the last search context (sector / city / keyword) so the
 * UpgradeModal can personalize its "before/after" preview with real leads
 * from the same niche the user just searched. Persisted to localStorage so
 * it survives navigations and page reloads.
 */
const SEARCH_CTX_KEY = "alp.last-search-ctx";

export function rememberSearchContext({ sector, city, keyword } = {}) {
  try {
    const ctx = {
      sector: (sector || "").trim().slice(0, 80),
      city: (city || "").trim().slice(0, 80),
      keyword: (keyword || "").trim().slice(0, 120),
      at: Date.now(),
    };
    if (ctx.sector || ctx.city || ctx.keyword) {
      window.localStorage.setItem(SEARCH_CTX_KEY, JSON.stringify(ctx));
    }
  } catch (_e) { /* localStorage not available */ }
}

export function readSearchContext() {
  try {
    const raw = window.localStorage.getItem(SEARCH_CTX_KEY);
    if (!raw) return {};
    const ctx = JSON.parse(raw);
    // Expire after 30 minutes — stale context is worse than no context.
    if (Date.now() - (ctx.at || 0) > 30 * 60 * 1000) return {};
    return { sector: ctx.sector, city: ctx.city, keyword: ctx.keyword };
  } catch (_e) { return {}; }
}
